import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Row,
  Col,
  Typography,
  Divider,
  Input,
  Button,
  Tag,
  Spin,
  notification,
} from "antd";
import { onValue, ref as dbRef } from "firebase/database";
import { sendPasswordResetEmail } from "firebase/auth";
import { auth, rtdb } from "../../../firebase.config";
import { UserContext } from "../../../context/UserContext";

const { Title, Text, Link } = Typography;

function maskPhone(phone) {
  if (!phone) return "";
  const digits = phone.replace(/\D+/g, "");
  if (digits.length < 4) return phone;
  const last4 = digits.slice(-4);
  return `XXXXXXX${last4}`;
}

export default function LandlordSettingsPage() {
  const navigate = useNavigate();
  const { user } = useContext(UserContext); // assumes user.uid, user.email
  const [formData, setFormData] = useState({ email: "", phone: "" });
  const [loading, setLoading] = useState(true);
  const [api, contextHolder] = notification.useNotification();

  // Listen to landlord profile in RTDB
  useEffect(() => {
    if (!user?.uid) return;
    const profileRef = dbRef(rtdb, `landlords/${user.uid}`);
    const unsub = onValue(
      profileRef,
      (snap) => {
        const val = snap.val() || {};
        setFormData({
          email: val.email || user.email || "",
          phone: val.phone || "",
          firstname: val.firstname || "",
          lastname: val.lastname || "",
          username: val.username || "",
        });
        setLoading(false);
      },
      () => setLoading(false)
    );
    return () => unsub();
  }, [user?.uid, user?.email]);

  const emailVerified = !!auth.currentUser?.emailVerified;
  const maskedPhone = useMemo(
    () => maskPhone(formData.phone),
    [formData.phone]
  );

  const handlePasswordReset = async () => {
    try {
      const email = formData.email || user?.email;
      if (!email) {
        api.error({
          message: "No email on file",
          description: "Cannot send password reset without an email address.",
        });
        return;
      }
      await sendPasswordResetEmail(auth, email);
      api.success({
        message: "Password reset sent",
        description: `We emailed a password reset link to ${email}.`,
      });
    } catch (err) {
      api.error({
        message: "Failed to send reset email",
        description: err?.message || String(err),
      });
    }
  };

  if (loading) {
    return (
      <div style={{ padding: 24 }}>
        <Spin />
      </div>
    );
  }

  return (
    <div style={{ padding: 24, maxWidth: 800 }}>
      {contextHolder}
      <Title level={3}>Account Settings</Title>

      {/* Email Section */}
      <Divider />
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Title level={5}>
            Email <Text type="danger">*</Text>
          </Title>
          <Input disabled value={formData.email} />
          <Text type="secondary" style={{ display: "block", marginTop: 4 }}>
            You will need to verify that you have access to this email. If your
            email address is updated at any point, you’ll need to re-verify it.
          </Text>
          <div style={{ display: "flex", alignItems: "center", marginTop: 8 }}>
            <Tag color={emailVerified ? "green" : "orange"}>
              {emailVerified ? "Verified" : "Not verified"}
            </Tag>
            <Button disabled style={{ marginLeft: "auto" }}>
              Update Email
            </Button>
          </div>
        </Col>

        {/* Phone */}
        <Col span={24}>
          <Title level={5}>Phone Number</Title>
          <Text type="secondary" style={{ display: "block" }}>
            Keep your phone up to date to secure your account and receive
            important alerts.
          </Text>

          <div style={{ display: "flex", alignItems: "center", marginTop: 8 }}>
            <Input
              disabled
              value={maskedPhone || "Not set"}
              style={{ width: 200 }}
            />
            <Button
              style={{ marginLeft: "auto" }}
              onClick={() => navigate("/landlord/phone-change")}
            >
              Change Phone
            </Button>
          </div>

          {/* Phone Verification */}
          <div style={{ marginTop: 16 }}>
            <Typography.Link
              onClick={() => navigate("/landlord/phone-verification")}
            >
              Verify your phone number
            </Typography.Link>
          </div>
        </Col>

        {/* Password Section */}
        <Col span={24}>
          <Title level={5}>Update Password</Title>
          <Text type="secondary" style={{ display: "block", marginBottom: 8 }}>
            Request password change
          </Text>
          <div style={{ marginTop: 8, textAlign: "right" }}>
            <Button onClick={handlePasswordReset}>Update Password</Button>
          </div>
        </Col>
      </Row>
    </div>
  );
}
